import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import type { GuideProduct, GuideSection, LegalSource } from '@/content/seo/guides';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import { Href, Link } from 'expo-router';
import React, { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { Reveal } from './seo-blocks';

const WA_WALLPAPER = '#ECE5DD';
const WA_BUBBLE_OUT = '#DCF8C6';

/** Mensagem pronta como balão de WhatsApp, com botão de copiar. */
export function MessageTemplate({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await Clipboard.setStringAsync(text);
    setCopied(true);
  };

  return (
    <View className="overflow-hidden rounded-2xl border border-neutral-900/5">
      <View className="flex-row items-center justify-between bg-card px-4 py-3">
        <ThemedText className="font-semibold text-sm">Mensagem pronta</ThemedText>
        <Pressable
          onPress={copy}
          accessibilityRole="button"
          className="h-8 flex-row items-center gap-1.5 rounded-full bg-primary-50 px-3"
        >
          <Ionicons name={copied ? 'checkmark' : 'copy-outline'} size={14} color="#CC3C00" />
          <Text className="font-semibold text-xs text-primary-700">{copied ? 'Copiada' : 'Copiar'}</Text>
        </Pressable>
      </View>
      <View className="p-4" style={{ backgroundColor: WA_WALLPAPER }}>
        <View className="self-end rounded-2xl rounded-tr-sm px-3 py-2" style={{ backgroundColor: WA_BUBBLE_OUT }}>
          <ThemedText selectable className="text-sm text-neutral-800">
            {text}
          </ThemedText>
        </View>
      </View>
    </View>
  );
}

/** Lista de vistos curtos. */
export function CheckList({ items }: { items: string[] }) {
  return (
    <View className="gap-3">
      {items.map((item) => (
        <View key={item} className="flex-row gap-3">
          <Ionicons name="checkmark-circle" size={20} color="#FD5001" style={{ marginTop: 2 }} />
          <ThemedText className="flex-1 text-base text-neutral-700">{item}</ThemedText>
        </View>
      ))}
    </View>
  );
}

/** "Fontes": as leis citadas na seção, com link para o texto oficial. */
export function LegalSources({ sources }: { sources: LegalSource[] }) {
  return (
    <View className="mt-5 gap-2 border-t border-neutral-100 pt-4">
      <ThemedText className="font-semibold text-sm text-neutral-700">Fontes</ThemedText>
      {sources.map((source) => (
        <Link key={`${source.law}-${source.article}`} href={source.url as Href}>
          <ThemedText className="text-sm text-primary-700 underline">
            {source.law}, {source.article}
          </ThemedText>
        </Link>
      ))}
    </View>
  );
}

/**
 * Cartão numerado de uma parte do guia: a pergunta como título, a resposta
 * curta (`capsule`) logo abaixo, os passos em vistos, os parágrafos à vista
 * (nada escondido atrás de "Entenda": a ressalva do que é pago e a da lei
 * precisam ser lidas), o modelo de mensagem como balão e as fontes da lei.
 */
export function GuideSectionCard({
  section,
  index,
  children,
}: {
  section: GuideSection;
  index: number;
  children?: React.ReactNode;
}) {
  const paragraphs = section.paragraphs ?? [];

  return (
    <Reveal className="rounded-2xl border border-neutral-900/5 bg-card p-4 md:p-8">
      <View className="mb-4 flex-row items-center gap-3">
        <View className="h-8 w-8 items-center justify-center rounded-full bg-primary">
          <ThemedText className="font-bold text-sm text-white">{index + 1}</ThemedText>
        </View>
        <ThemedText role="heading" aria-level={2} className="flex-1 font-bold text-xl">
          {section.heading}
        </ThemedText>
      </View>
      {section.capsule ? (
        <ThemedText className="mb-5 font-medium text-base text-neutral-900">{section.capsule}</ThemedText>
      ) : null}
      {section.steps ? <CheckList items={section.steps} /> : null}
      {paragraphs.length > 0 ? (
        <View className={`gap-3 ${section.steps ? 'mt-5' : ''}`}>
          {paragraphs.map((p) => (
            <ThemedText key={p} className="text-base leading-7 text-neutral-700">
              {p}
            </ThemedText>
          ))}
        </View>
      ) : null}
      {section.template ? (
        <View className="mt-5">
          <MessageTemplate text={section.template} />
        </View>
      ) : null}
      {section.legalSources?.length ? <LegalSources sources={section.legalSources} /> : null}
      {children}
    </Reveal>
  );
}

/**
 * "Como a Controlapag faz isso?": o produto, separado do conselho geral, com o
 * número real do app e o que é pago sempre à vista.
 */
export function GuideProductCard({ heading, product }: { heading: string; product: GuideProduct }) {
  return (
    <Reveal className="gap-5 rounded-2xl bg-ink p-5 md:p-8">
      <ThemedText role="heading" aria-level={2} className="font-bold text-xl text-white">
        {heading}
      </ThemedText>
      <ThemedText className="font-medium text-base text-white">{product.capsule}</ThemedText>
      <View className="gap-3">
        {product.steps.map((step) => (
          <View key={step} className="flex-row gap-3">
            <Ionicons name="checkmark-circle" size={20} color="#FD5001" style={{ marginTop: 2 }} />
            <ThemedText className="flex-1 text-base text-neutral-300">{step}</ThemedText>
          </View>
        ))}
      </View>
      <ThemedText className="text-sm text-neutral-400">{product.paidNote}</ThemedText>
      <Link href="/(auth)/register" asChild>
        <Button size="sm" title="Crie sua conta grátis e conheça" className="self-start" />
      </Link>
    </Reveal>
  );
}
